// Preferences page -- iteration 073
// Edit user preferences after onboarding (reuses the same pickers).

import { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import * as api from "@/lib/api";
import type { UserPreferencePublic, SportPublic, AgeGroupPublic, NotificationPreferences } from "@dugads/shared";
import { FollowedSchoolsPicker, SportsPicker, AgeGroupsPicker, NotificationTogglesPicker } from "@/components/preferences-pickers";

export default function PreferencesPage() {
  const [prefs, setPrefs] = useState<UserPreferencePublic | null>(null);
  const [sports, setSports] = useState<SportPublic[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroupPublic[]>([]);
  const [schools, setSchools] = useState<{ id: string; name: string; display_name: string | null }[]>([]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.getPreferences(),
      api.getSports(),
      api.getAllAgeGroups(),
    ]).then(([p, s, ag]) => {
      setPrefs(p);
      setSports(s.sports);
      setAgeGroups(ag.age_groups);

      // Fetch school names for followed schools
      if (p.followed_school_ids.length > 0) {
        Promise.all(p.followed_school_ids.map((id) => api.getSchool(id).catch(() => null)))
          .then((results) => {
            setSchools(results.filter((r): r is NonNullable<typeof r> => r !== null).map((r) => ({
              id: r.school.id,
              name: r.school.name,
              display_name: r.school.display_name,
              logo_url: r.school.logo_url,
              primary_color: r.school.primary_color,
            })));
          });
      }
    }).catch((err) => setError(err.message ?? "Failed to load"));
  }, []);

  async function update(patch: Partial<UserPreferencePublic>) {
    if (!prefs) return;
    setSaving(true);
    setSuccess(false);
    setError("");
    try {
      const next = await api.updatePreferences(patch);
      setPrefs(next);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  if (!prefs) {
    return (
      <div data-page="preferences" className="mx-auto max-w-2xl p-6">
        <h1 className="text-2xl font-bold mb-4">Preferences</h1>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div data-page="preferences" className="mx-auto max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Preferences</h1>
        {saving && <span className="text-xs text-muted-foreground">Saving...</span>}
        {success && <span className="text-xs text-green-600">Saved</span>}
      </div>

      {error && <p className="text-destructive text-sm mb-4">{error}</p>}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Follow Schools</CardTitle>
          <CardDescription>Get updates from additional schools beyond your own affiliations</CardDescription>
        </CardHeader>
        <CardContent>
          <FollowedSchoolsPicker
            selectedIds={prefs.followed_school_ids}
            selectedSchools={schools}
            onChange={(ids, schoolList) => {
              setSchools(schoolList);
              update({ followed_school_ids: ids });
            }}
          />
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Sports of Interest</CardTitle>
          <CardDescription>Filter events and calendar by these sports</CardDescription>
        </CardHeader>
        <CardContent>
          <SportsPicker
            sports={sports}
            selectedIds={prefs.sport_ids}
            onChange={(ids) => update({ sport_ids: ids })}
          />
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Age Groups</CardTitle>
          <CardDescription>Filter by age groups within your selected sports</CardDescription>
        </CardHeader>
        <CardContent>
          <AgeGroupsPicker
            ageGroups={ageGroups.filter((ag) => prefs.sport_ids.length === 0 || prefs.sport_ids.includes(ag.sport_id))}
            selectedIds={prefs.age_group_ids}
            onChange={(ids) => update({ age_group_ids: ids })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Notifications</CardTitle>
          <CardDescription>Choose which push notifications to receive</CardDescription>
        </CardHeader>
        <CardContent>
          <NotificationTogglesPicker
            prefs={prefs.notification_preferences}
            onChange={(np: NotificationPreferences) => update({ notification_preferences: np })}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// Re-export so App.tsx can import the Button type-check -- unused but keeps future refactors safe.
export { Button };
